import { sha256HexLower } from './hash';
import { marketingAllowed } from './supportSecurity';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}


export const FB_PIXEL_ID = '1032695039018851';

let inited = false;

// Ensure the pixel base code is loaded once
let loaderPromise: Promise<void> | null = null;

export const loadPixel = (): Promise<void> => {
  if (!marketingAllowed()) return Promise.resolve();
  if (window.fbq) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve) => {
    try {
      // Standard Meta Pixel base code injection
      (function (f: any, b: Document, e: string, v: string, n?: any, t?: HTMLScriptElement, s?: Element) {
        if (f.fbq) {
          resolve();
          return;
        }
        n = f.fbq = function () {
          if (n.callMethod) {
            n.callMethod.apply(n, arguments);
          } else {
            n.queue.push(arguments);
          }
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          b.head?.appendChild(t);
        }
        // We resolve immediately after stub creation so callers can queue events;
        // the script will flush the queue once loaded.
        resolve();
      })(window as any, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    } catch {
      resolve();
    }
  });

  return loaderPromise;
};

// Helper to guarantee pixel is loaded and initialized with Advanced Matching once
export const ensureInit = async (
  email?: string | null,
  externalId?: string | null,
  extras?: Partial<AdvancedMatch>
): Promise<void> => {
  await loadPixel();
  await init(email, externalId, extras);
};

type AdvancedMatch = {
  em?: string;
  external_id?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
};

type FbEventParams = Record<string, unknown> & {
  eventID?: string;
};

export const init = async (
  email?: string | null,
  externalId?: string | null,
  extras?: Partial<AdvancedMatch>
): Promise<void> => {
  if (!marketingAllowed() || !window.fbq) return;
  if (inited) return;

  const advancedMatching: AdvancedMatch = {};
  if (email) advancedMatching.em = await sha256HexLower(email);
  if (externalId) advancedMatching.external_id = String(externalId).trim();
  if (extras) Object.assign(advancedMatching, extras);

  if (!marketingAllowed() || !window.fbq) return;
  window.fbq('init', FB_PIXEL_ID, advancedMatching);
  inited = true;
};

export const generateEventId = (eventName: string): string => {
  const rand = Math.random().toString(36).slice(2, 11);
  return `${eventName}_${Date.now()}_${rand}`;
};

export const pageview = (): void => {
  if (marketingAllowed() && window.fbq) {
    window.fbq('track', 'PageView');
  }
};

export const event = (
  name: string,
  parameters?: FbEventParams,
  eventId?: string
): void => {
  try {
    if (marketingAllowed() && window.fbq) {
      const cleanParams: FbEventParams = { ...(parameters || {}) };
      // Ensure no unhashed PII is ever sent in event params
      delete (cleanParams as any).email;
      delete (cleanParams as any).external_id;

      const params: FbEventParams = {
        ...cleanParams,
        ...(eventId ? { eventID: eventId } : {}),
      };
      window.fbq('track', name, params);
    }
  } catch {
    // swallow
  }
};

export const waitForFbPixel = (): Promise<void> => {
  // Keep API compatibility but rely on loader which creates fbq stub and injects script.
  return loadPixel();
};
